import { useMemo, Fragment, type ReactNode } from 'react';
import { parseChatMarkdown } from './markdownBlocks';
import type { Span } from './markdownBlocks';

/**
 * An agent's chat reply, with its markdown rendered.
 *
 * Shared by every chat pane — the discovery keyword chat, the knowledge panel and the
 * structure planner each had their own copy of `<div>{message.content}</div>`, and so
 * each had the same bug: asterisks on screen, lists as a run-on line.
 *
 * Spans become TEXT NODES, never HTML. Nothing a model writes can inject markup here,
 * which is why there is no `dangerouslySetInnerHTML` anywhere near this file.
 */
export default function ChatMarkdown({
  text,
  className = '',
  renderText,
}: {
  text: string;
  className?: string;
  /** How plain text is drawn, for a pane that makes some of it clickable (a
   *  room chat's "#36948" opens that item). Never applied inside code. */
  renderText?: (text: string) => ReactNode;
}) {
  const blocks = useMemo(() => parseChatMarkdown(text), [text]);

  return (
    <div className={`space-y-2 break-words ${className}`}>
      {blocks.map((block, i) => {
        if (block.kind === 'h') {
          return (
            <p key={i} className="font-semibold">
              <Spans spans={block.spans} renderText={renderText} />
            </p>
          );
        }
        if (block.kind === 'p') {
          return (
            // pre-wrap, as the raw version had: an agent that hard-wraps its own prose
            // meant those breaks, and joining them re-flows a deliberate shape.
            <p key={i} className="whitespace-pre-wrap">
              <Spans spans={block.spans} renderText={renderText} />
            </p>
          );
        }
        const List = block.kind === 'ol' ? 'ol' : 'ul';
        return (
          <List
            key={i}
            className={`pl-5 space-y-0.5 ${
              block.kind === 'ol' ? 'list-decimal' : 'list-disc'
            }`}
          >
            {block.items.map((item, j) => (
              <li key={j}>
                <Spans spans={item} renderText={renderText} />
              </li>
            ))}
          </List>
        );
      })}
    </div>
  );
}

function Spans({ spans, renderText }: { spans: Span[]; renderText?: (text: string) => ReactNode }) {
  return (
    <>
      {spans.map((s, i) => {
        let el = <>{renderText && !s.code ? renderText(s.text) : s.text}</>;
        if (s.code) {
          el = (
            <code className="px-1 py-0.5 rounded bg-black/10 dark:bg-white/10 font-mono text-[0.9em]">
              {s.text}
            </code>
          );
        }
        if (s.italic) el = <em>{el}</em>;
        if (s.bold) el = <strong className="font-semibold">{el}</strong>;
        return <Fragment key={i}>{el}</Fragment>;
      })}
    </>
  );
}
